// Shared visual treatment for the origin/destination Autocomplete dropdowns
// used in SearchForm + CompactSearchForm. Lifts the look from the default MUI
// list to something that matches the FlyBnB brand: glassmorphic panel, brand
// border tint, soft shadow, two-line option rows with a country flag emoji.
//
// Usage on each <Autocomplete>:
//   renderOption={renderCityOption}
//   slotProps={cityAutocompleteSlotProps(theme)}
//   noOptionsText={<NoCitiesFound />}

import React from "react";
import { Box, Typography } from "@mui/material";

// Two-letter ISO country code → flagcdn.com PNG URL (works on all platforms)
export function countryFlagUrl(cc) {
  if (!cc || typeof cc !== "string" || cc.length !== 2) return null;
  return `https://flagcdn.com/20x15/${cc.toLowerCase()}.png`;
}

// Build the location subtitle: "Île-de-France, France" or "California, US"
function locationLine(opt) {
  const useAdmin = opt.country_code === "US" || opt.country_code === "CA";
  const parts = useAdmin
    ? [opt.admin, opt.country_name]
    : [opt.country_name];
  return parts.filter(Boolean).join(", ");
}

// renderOption signature: (props, option) — props includes key + onClick etc.
export function renderCityOption(props, option) {
  const { key, ...liProps } = props;
  const flagUrl = countryFlagUrl(option.country_code);
  const loc = locationLine(option);

  return (
    <Box
      component="li"
      key={key}
      {...liProps}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        cursor: "pointer",
        borderRadius: "10px",
        mx: 0.75,
        my: 0.25,
        transition: "background-color 120ms ease, transform 120ms ease",
        "&[aria-selected='true']": {
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(126,120,210,0.18)"
              : "rgba(95,90,162,0.10)",
        },
        "&.Mui-focused, &:hover": {
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-hidden
      >
        {flagUrl ? (
          <img
            src={flagUrl}
            alt=""
            width={20}
            height={15}
            style={{ display: "block", borderRadius: 2 }}
          />
        ) : (
          <span style={{ fontSize: 18 }}>📍</span>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {option.city}
        </Typography>
        {loc && (
          <Typography
            component="div"
            sx={{
              fontSize: 11.5,
              color: "text.secondary",
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {loc}
          </Typography>
        )}
      </Box>

    </Box>
  );
}

// slotProps for the dropdown's Paper + Listbox so the panel matches brand.
export function cityAutocompleteSlotProps(theme) {
  const dark = theme.palette.mode === "dark";
  return {
    paper: {
      sx: {
        mt: 0.75,
        borderRadius: "14px",
        overflow: "hidden",
        border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
        bgcolor: dark ? "rgba(22,22,28,0.92)" : "rgba(255,255,255,0.96)",
        backdropFilter: "saturate(180%) blur(14px)",
        WebkitBackdropFilter: "saturate(180%) blur(14px)",
        boxShadow: dark
          ? "0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(126,120,210,0.10) inset"
          : "0 20px 50px rgba(95,90,162,0.18), 0 0 0 1px rgba(95,90,162,0.05) inset",
      },
    },
    listbox: {
      sx: {
        py: 0.75,
        maxHeight: 380,
        // Custom scrollbar — subtle, brand-tinted
        scrollbarWidth: "thin",
        scrollbarColor: dark
          ? "rgba(126,120,210,0.35) transparent"
          : "rgba(95,90,162,0.3) transparent",
        "&::-webkit-scrollbar": { width: 8 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: dark ? "rgba(126,120,210,0.35)" : "rgba(95,90,162,0.3)",
          borderRadius: 999,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: dark ? "rgba(126,120,210,0.55)" : "rgba(95,90,162,0.5)",
        },
        // Hide MUI's default option padding — we render our own <li>
        "& .MuiAutocomplete-option": { padding: 0 },
      },
    },
  };
}
